import { useEffect, useMemo, useState } from 'react'

import { TreeItemIndex } from 'react-complex-tree'

import { useI18n } from '@tribeplatform/react-components/i18n'

import { DEFAULT_TREE_ITEM_LIMIT } from '../constants.js'
import {
  createShowOrLessItem,
  showOrLessIndex,
} from '../tree-items/createShowOrLessTreeItem.js'
import { TreeItems } from '../types.js'

export function useLimitTreeItems(
  id: TreeItemIndex,
  children: TreeItemIndex[],
  {
    limit = DEFAULT_TREE_ITEM_LIMIT,
    ensureVisible,
  }: {
    limit?: number
    ensureVisible?: TreeItemIndex
  } = {},
) {
  const intl = useI18n()
  const [showOrLess, setShowOrLess] = useState<'show' | 'less'>('less')

  useEffect(() => {
    if (
      ensureVisible &&
      children.includes(ensureVisible) &&
      children.indexOf(ensureVisible) >= limit
    ) {
      setShowOrLess('show')
    }
  }, [ensureVisible, children, limit])

  const showOrLessItem = useMemo(() => {
    return createShowOrLessItem(intl, {
      id,
      showOrLess,
      setShowOrLess,
    })
  }, [intl, showOrLess, id, setShowOrLess])

  const items: { children: TreeItemIndex[]; items: TreeItems } = useMemo(() => {
    if (!children || children.length <= limit) {
      return {
        children,
        items: {},
      }
    }

    if (showOrLess === 'show') {
      return {
        children: [...children, showOrLessIndex(id)],
        items: showOrLessItem,
      }
    }

    return {
      children: [...children.slice(0, limit), showOrLessIndex(id)],
      items: showOrLessItem,
    }
  }, [children, showOrLess, showOrLessItem, limit, id])

  return items
}
